import {useEffect, useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import {Alert, Form} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {DateTime} from 'luxon';
import TableMetronic from '../../../../../app/common/components/table/TableMetronic';
import ActionIcon from '../../../../../Components/ActionIcon';
import Badge from '../../../../../app/common/components/Badge';
import Loading from '../../../../../app/common/components/Loading';
import {ApiVoicemailMessage} from '../../../Api/telephonie';
import {
  useDeleteVoicemailMessage,
  useGetTrunkNumeros,
  useGetVoicemailMessageAudio,
  useGetVoicemailMessages,
} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';

interface GererMessagesVocauxProps {
  serviceId: number;
  isActive?: boolean;
}

/**
 * Messages vocaux enregistrés (voicemail) d'un numéro Trunk SIP — à ne pas
 * confondre avec l'annonce personnelle (ModifierBoiteVocale, onglet « Gérer
 * vos numéros ») : ici ce sont les messages laissés par les appelants.
 */
const GererMessagesVocaux = ({
  serviceId,
  isActive = true,
}: GererMessagesVocauxProps): JSX.Element => {
  const intl = useIntl();
  const {data: numerosData} = useGetTrunkNumeros(serviceId, isActive);
  // Seuls les numéros actifs ont une boîte vocale à consulter.
  const numeros = useMemo(
    () => (numerosData?.numeros ?? []).filter((numero) => numero.actif),
    [numerosData]
  );
  const [numeroId, setNumeroId] = useState<number | null>(null);
  const [message, setMessage] = useAutoDismissMessage();
  const [lecture, setLecture] = useState<{messageId: number; audioContent: string} | null>(null);

  useEffect(() => {
    if (numeroId === null && numeros.length > 0) {
      setNumeroId((numeros.find((numero) => numero.principal) ?? numeros[0]).numeroId);
    }
  }, [numeros, numeroId]);

  const {
    data: messages,
    isLoading,
    isError,
  } = useGetVoicemailMessages(serviceId, numeroId ?? 0, isActive && numeroId !== null);
  const {mutate: chargerAudio, isLoading: chargementAudio} = useGetVoicemailMessageAudio(
    serviceId,
    numeroId ?? 0
  );
  const {mutate: supprimerMutation} = useDeleteVoicemailMessage(serviceId, numeroId ?? 0);

  const ecouter = (voicemailMessage: ApiVoicemailMessage): void => {
    setLecture(null);
    chargerAudio(voicemailMessage.id, {
      onSuccess: (audio) => {
        if (audio.audioContent) {
          setLecture({messageId: voicemailMessage.id, audioContent: audio.audioContent});
        } else {
          setMessage(intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.AUCUN_AUDIO'}));
        }
      },
      onError: () => setMessage(intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})),
    });
  };

  const supprimer = (voicemailMessage: ApiVoicemailMessage): void => {
    const confirme = window.confirm(
      intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.CONFIRMER_SUPPRESSION'})
    );
    if (!confirme) {
      return;
    }
    if (lecture?.messageId === voicemailMessage.id) {
      setLecture(null);
    }
    supprimerMutation(voicemailMessage.id, {
      onError: () => setMessage(intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})),
    });
  };

  const columns = useMemo<ColumnDef<ApiVoicemailMessage>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.COLONNE.DATE'}),
        id: 'createdOn',
        size: 170,
        cell: (info) => {
          const {createdOn} = info.row.original;
          return createdOn ? DateTime.fromISO(createdOn).toFormat('dd/MM/yyyy HH:mm') : '';
        },
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.COLONNE.APPELANT'}),
        id: 'peer',
        cell: (info) => {
          const {peerName, peerUri} = info.row.original;
          return peerName || peerUri || '';
        },
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.COLONNE.DUREE'}),
        id: 'duration',
        size: 100,
        cell: (info) => {
          const {duration} = info.row.original;
          return duration != null ? `${duration}s` : '';
        },
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.COLONNE.STATUT'}),
        id: 'flag',
        size: 110,
        cell: (info) =>
          info.row.original.flag === 'NEW' ? (
            <Badge
              text={intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.STATUT.NOUVEAU'})}
              color="primary"
              light
              size="small"
            />
          ) : (
            ''
          ),
      },
      {
        header: intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.ACTIONS'}),
        id: 'actions',
        size: 110,
        enableSorting: false,
        cell: (info) => {
          const voicemailMessage = info.row.original;
          return (
            <div>
              <ActionIcon
                label={intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.ECOUTER'})}
                faIcon="fs-3 bi bi-play-circle"
                onClick={() => ecouter(voicemailMessage)}
                disabled={chargementAudio}
              />
              <ActionIcon
                label={intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.SUPPRIMER'})}
                faIcon="fs-3 bi bi-trash text-danger"
                onClick={() => supprimer(voicemailMessage)}
              />
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, chargementAudio, lecture]
  );

  if (numerosData && numeros.length === 0) {
    return (
      <p className="text-muted mt-4">
        {intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.AUCUN_NUMERO'})}
      </p>
    );
  }

  return (
    <div className="mt-4">
      {message && <div className="fw-bold text-danger mb-3">{message}</div>}

      <Form.Label className="fw-bold fs-6">
        {intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.NUMERO'})}
      </Form.Label>
      <Form.Select
        style={{maxWidth: 320}}
        className="mb-4"
        value={numeroId ?? ''}
        onChange={(e) => {
          setNumeroId(Number(e.target.value));
          setLecture(null);
        }}
      >
        {numeros.map((numero) => (
          <option key={numero.numeroId} value={numero.numeroId}>
            {numero.numero}
          </option>
        ))}
      </Form.Select>

      {lecture && (
        <Alert variant="light" className="d-flex align-items-center gap-3">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls autoPlay src={`data:audio/wav;base64,${lecture.audioContent}`} />
          <ActionIcon
            label={intl.formatMessage({id: 'COMMON.CLOSE'})}
            faIcon="fs-3 bi bi-x-lg"
            onClick={() => setLecture(null)}
          />
        </Alert>
      )}

      {isError && <p>{intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})}</p>}

      {isLoading && <Loading className="col-md-6" />}

      {!isLoading && !isError && (
        <TableMetronic<ApiVoicemailMessage>
          title={intl.formatMessage({id: 'TELEPHONIE.MESSAGES.VOCAUX.TABLE_TITLE'})}
          data={messages ?? []}
          columns={columns}
          searchBar={false}
          stackedHeader
          noRowsMessage={intl.formatMessage({id: 'TELEPHONIE.MESSAGES.AUCUN_MESSAGE'})}
        />
      )}
    </div>
  );
};

export default GererMessagesVocaux;
